import { useCallback, useState } from 'react';

const KEY = 'sidebar_collapsed';
const ONE_YEAR = 60 * 60 * 24 * 365;

function cookieAttrs(): string {
  const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN || '';
  const domain = cookieDomain ? `; domain=${cookieDomain}` : '';
  const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';
  const sameSite = isSecure ? '; SameSite=None; Secure' : '';
  return `${domain}${sameSite}`;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const cookie of document.cookie.split(';')) {
    const separator = cookie.indexOf('=');
    if (separator === -1) continue;
    const key = cookie.slice(0, separator).trim();
    if (key === name) return decodeURIComponent(cookie.slice(separator + 1));
  }
  return null;
}

function storedValue(): '0' | '1' | null {
  const cookie = readCookie(KEY);
  if (cookie === '0' || cookie === '1') return cookie;
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === '0' || saved === '1') return saved;
  } catch {
    return null;
  }
  return null;
}

export function readSidebarCollapsed(): boolean | null {
  const value = storedValue();
  if (value === '1') return true;
  if (value === '0') return false;
  return null;
}

export function writeSidebarCollapsed(collapsed: boolean) {
  const value = collapsed ? '1' : '0';
  if (typeof document !== 'undefined') {
    document.cookie = `${KEY}=${value}; path=/; max-age=${ONE_YEAR}${cookieAttrs()}`;
  }
  try {
    localStorage.setItem(KEY, value);
  } catch {
    // Private mode can block storage. The cookie still carries the choice.
  }
}

export function useSidebarCollapsed(fallback = false) {
  const [collapsed, setCollapsed] = useState(() => readSidebarCollapsed() ?? fallback);
  const onCollapsedChange = useCallback((next: boolean) => {
    setCollapsed(next);
    writeSidebarCollapsed(next);
  }, []);
  return { collapsed, onCollapsedChange };
}
